import { useRef, useState } from "react";
import { DialogTrigger, Pressable } from "react-aria-components";
import { Bug, LogOut, Moon, Settings as SettingsIcon, Sun, SunMoon, Users } from "lucide-react";
import { useNavigate } from "react-router";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { cn } from "@/utils/helpers";
import { KitPopover } from "./kit/Popover";
import { KitTooltip, KitTooltipTrigger } from "./kit/Tooltip";
import UserMark from "./UserMark";
import { SignOutConfirm } from "@/components/gettingIn/SessionDialogs";


/** Row (library 01, Nav / Profile menu): 40 tall, 8 in, gap 12, radius 10,
 *  Manrope 500 13 in co-text; a 15 glyph leads when the row has one. */
const rowClass = cn(
  "flex h-10 w-full shrink-0 items-center gap-3 rounded-[10px] px-2 text-left text-[13px] font-medium leading-none text-co-text",
  "transition-colors hover:bg-co-hover",
  "focus-visible:outline-2 focus-visible:outline-co-accent focus-visible:outline-offset-[-2px]",
);
/** The trailing value on a row: JetBrains Mono 10 in co-muted. */
const valueClass = "shrink-0 font-mono text-[10px] leading-none uppercase text-co-muted";
const rule = <div aria-hidden className="h-px w-full shrink-0 bg-co-line" />;

/** Nav / Appearance switch (library 01, 210:25628): two 38×30 icon segments on
 *  control glass at 3; the chosen one sits on the lens with the ink glyph,
 *  the other is muted. Each segment is named and has a tooltip. */
function AppearanceSwitch({ theme, onChange }: { theme: "light" | "dark"; onChange: (t: "light" | "dark") => void }) {
  const options = [
    { value: "dark", label: "Dark", Icon: Moon },
    { value: "light", label: "Light", Icon: Sun },
  ] as const;
  const index = theme === "dark" ? 0 : 1;
  return (
    <div role="radiogroup" aria-label="Appearance" className="co-glass-control relative inline-flex shrink-0 rounded-pill p-[3px]">
      <span
        aria-hidden="true"
        className="co-seg-lens pointer-events-none absolute left-[3px] top-[3px] h-[30px] w-[38px]"
        style={{ transform: `translateX(${index * 38}px)` }}
      />
      {options.map(({ value, label, Icon }) => {
        const selected = value === theme;
        return (
          <KitTooltipTrigger key={value} delay={400}>
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={label}
              onClick={() => onChange(value)}
              className={cn(
                "relative z-[1] grid h-[30px] w-[38px] cursor-pointer place-items-center rounded-pill outline-none",
                "transition-colors duration-[var(--duration-fast)] ease-out",
                "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
                selected ? "text-co-text" : "text-co-muted hover:text-co-text",
              )}
            >
              <Icon size={16} aria-hidden />
            </button>
            <KitTooltip placement="top">{label}</KitTooltip>
          </KitTooltipTrigger>
        );
      })}
    </div>
  );
}

export default function ProfileMenu({
  /** Collapsed rail: the mark is the only identity left, and it still works. */
  compact = false,
  invitesPending = 0,
  onNavigate,
  theme,
  onToggleTheme,
  onOpenFeedback,
}: {
  compact?: boolean;
  invitesPending?: number;
  onNavigate?: () => void;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  /** Opens the global bug / recommendation modal. */
  onOpenFeedback?: () => void;
}) {
  const { user, selectedOrganization, logout } = useCurrentUser();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /* Folded (Figma 11 · Shell 2): beside the avatar, foot-aligned, 10 off the
     rail. Open rail or drawer: 8 above the profile row. */
  const [offset, setOffset] = useState(10);
  const onOpenChange = (next: boolean) => {
    if (next && compact) {
      const t = triggerRef.current?.getBoundingClientRect();
      const rail = triggerRef.current?.closest("#co-nav-rail")?.getBoundingClientRect();
      if (t && rail) setOffset(rail.right - 1 + 10 - t.right);
    }
    setOpen(next);
  };

  if (!user) return null;

  const orgCount = user.userOrganizations?.length ?? 0;
  const org = selectedOrganization?.organization;
  const role = selectedOrganization?.role;

  const go = (to: string) => {
    setOpen(false);
    onNavigate?.();
    navigate(to);
  };

  return (
    <>
      <DialogTrigger isOpen={open} onOpenChange={onOpenChange}>
        <Pressable>
          <button
            ref={triggerRef}
            type="button"
            aria-label={`Account, ${user.name}${invitesPending > 0 ? `, ${invitesPending} pending invitation${invitesPending === 1 ? "" : "s"}` : ""}`}
            aria-haspopup="dialog"
            aria-expanded={open}
            /* A 30 stop with the 26 avatar at 2, folded or open. The name
               grows to the right; the avatar does not move. */
            className={cn(
              "co-nav flex h-[30px] min-w-0 flex-none cursor-pointer items-center gap-2.5 px-0.5",
              "focus-visible:outline-2 focus-visible:outline-co-accent focus-visible:outline-offset-2",
              compact ? "w-[30px]" : "w-full",
            )}
          >
            <UserMark
              name={user.name}
              photoUrl={user.profilePicture}
              size={26}
              flagged={invitesPending > 0}
              rail
            />
            {compact ? null : (
              <span className="co-rail-text min-w-0 flex-1 truncate text-left text-[12.5px] leading-[17px] font-semibold text-co-text">{user.name}</span>
            )}
          </button>
        </Pressable>

        {/* Rises from the rail foot rather than dropping: the control lives at
            the bottom of the screen. Folded, it opens beside the avatar,
            bottom-aligned, 10 off the rail (library 01, 193:26011). */}
        <KitPopover
          placement={compact ? "right bottom" : "top start"}
          offset={compact ? offset : 8}
          containerPadding={12}
          className="co-chrome-sheet co-profile-menu w-[320px] max-w-[calc(100vw-24px)] p-0"
        >
          <div className="co-stagger flex w-full flex-col p-1.5">
            {/* You: 8 8 12 at gap 12, the 38 avatar, the name over the email. */}
            <div className="flex w-full items-center gap-3 px-2 pb-3 pt-2">
              <UserMark name={user.name} photoUrl={user.profilePicture} size={38} menu />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5 leading-none">
                <span className="truncate text-[13px] font-semibold text-co-text">{user.name}</span>
                {/* The email is the disambiguator when two people share a name. */}
                <span className="truncate font-mono text-[10px] text-co-muted">{user.email}</span>
              </span>
            </div>

            {org ? (
              <>
                {rule}
                <span className="pb-1 pl-2 pt-2.5 font-mono text-[10px] leading-none uppercase text-co-muted">
                  Organisation
                </span>
                <div className={cn(rowClass, "cursor-default hover:bg-transparent")}>
                  <span className="min-w-0 flex-1 truncate">{org.name}</span>
                  {/* Role is shown because it explains what you can and cannot do:
                      isOwner is what gates brand deletion. */}
                  <span className={valueClass}>{role}</span>
                </div>
                {orgCount > 1 ? (
                  <button type="button" onClick={() => go("/dashboard/settings?page=workspaces")} className={rowClass}>
                    <Users className="size-[15px] shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">Switch organisation</span>
                    <span className={valueClass}>{orgCount}</span>
                  </button>
                ) : null}
              </>
            ) : null}

            {rule}
            <button type="button" onClick={() => go("/dashboard/settings?page=workspaces")} className={rowClass}>
              <span className="min-w-0 flex-1 truncate">Invitations</span>
              {invitesPending > 0 ? (
                <span className={cn(valueClass, "text-co-accent")}>{invitesPending} new</span>
              ) : (
                <span className={valueClass}>None</span>
              )}
            </button>
            <button type="button" onClick={() => go("/dashboard/settings")} className={rowClass}>
              <SettingsIcon className="size-[15px] shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 truncate">Settings</span>
            </button>
            {/* Not in the frame: Feedback moved in from the rail foot with
                Appearance, and takes the same row. */}
            {onOpenFeedback ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onNavigate?.();
                  onOpenFeedback();
                }}
                className={rowClass}
              >
                <Bug className="size-[15px] shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 truncate">Feedback</span>
              </button>
            ) : null}
            <div className={cn(rowClass, "cursor-default hover:bg-transparent")}>
              <SunMoon className="size-[15px] shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 truncate">Appearance</span>
              <AppearanceSwitch theme={theme} onChange={(t) => t !== theme && onToggleTheme()} />
            </div>

            {rule}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setSignOutOpen(true);
              }}
              className={cn(rowClass, "text-co-danger")}
            >
              <LogOut className="size-[15px] shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 truncate">Sign out</span>
            </button>
          </div>
        </KitPopover>
      </DialogTrigger>

      <SignOutConfirm isOpen={signOutOpen} onOpenChange={setSignOutOpen} onConfirm={logout} />
    </>
  );
}
